import {
  SurveySettings,
  Submission,
} from '../../types';
import {
  CompleteEvent, CompletingEvent, CurrentPageChangingEvent, SurveyModel,
} from 'survey-core';
import { applySurveyTheme } from '../../styles/apply-survey-theme';
import { registerCustomSurveyQuestions } from './register-custom-questions';
import { uploadFileHandler } from '../../utils/file-upload/upload-file-handler';
import { deleteFileHandler } from '../../utils/file-upload/delete-file-handler';
import { setFileUploadFieldValues } from '../../utils/file-upload/set-default-field-values';
import Inputmask from 'inputmask';
import i18n from '../../i18n';

type PageChangeHandler = (sender: SurveyModel, options: CurrentPageChangingEvent) => Promise<void>;
type SurveyCompleteHandler = (sender: SurveyModel, options: CompleteEvent) => Promise<void>;
type SurveyCompletingHandler = (sender: SurveyModel, options: CompletingEvent) => Promise<void>;

export interface GetSurveyModelProps {
  settings: SurveySettings;
  submission?: Submission;
  onComplete?: SurveyCompleteHandler;
  onCompleting?: SurveyCompletingHandler;
  onCurrentPageChanging?: PageChangeHandler;
  completedHtml?: string;
  currentPage?: number;
  readOnly?: boolean;
  isSigner?: boolean;
  trackingId?: string;
}

export const getSurveyModel = ({
  settings,
  submission,
  onComplete,
  onCompleting,
  onCurrentPageChanging,
  completedHtml = '',
  currentPage = 0,
  readOnly = false,
  isSigner = false,
  trackingId = '',
}: GetSurveyModelProps) => {
  const surveyModel = new SurveyModel(settings.survey);

  surveyModel.currentPageNo = currentPage;
  surveyModel.data = submission?.responses ?? {};
  surveyModel.readOnly = readOnly;
  surveyModel.showCompleteButton = !readOnly;
  surveyModel.completedHtml = completedHtml;

  applySurveyTheme(surveyModel, settings);

  // TODO: Find a more elegant solution to this issue
  // This resolves an issue where applications with ToC enabled show
  // "No data to display" on refresh
  // This is a workaround to force a re-render of the table of contents
  surveyModel.onAfterRenderSurvey.add(() => {
    if (surveyModel.showTOC) {
      setTimeout(() => {
        surveyModel.render();
      }, 50);
    }
  });

  registerCustomSurveyQuestions({
    settings,
    submission,
    isSigner,
  });
  setFileUploadFieldValues();

  // Set up phone mask and phone validation
  // Moved from SurveyQuestions component to fix race condition
  surveyModel.onAfterRenderQuestion.add((_, options) => {
    const inputElement = options.htmlElement.querySelector('input');
    // Set up phone mask
    if (options.question.inputType === 'phone') {
      if (inputElement) {
        Inputmask({ mask: '(999) 999-9999' }).mask(inputElement);
      }
    }
  });
  surveyModel.onValidateQuestion.add((_, options) => {
    if (options.question.inputType === 'phone') {
      const digitsOnly = (options.value || '').replace(/\D/g, '');
      if (digitsOnly.length !== 10) {
        // Use i18n directly for translation
        options.error = i18n.t('components:survey-page.phone_number_error_message');
      }
    }
  });

  if (onComplete) {
    surveyModel.onComplete.add(onComplete);
  }

  if (onCompleting) {
    surveyModel.onCompleting.add(onCompleting);
  }

  if (onCurrentPageChanging) {
    surveyModel.onCurrentPageChanging.add(onCurrentPageChanging);
  }

  if (submission) {
    const urlSlug = settings.urlSlug;
    const publicId = submission.publicId;

    surveyModel.onUploadFiles.add(async (sender, options) => {
      await uploadFileHandler({
        options,
        urlSlug,
        publicId,
        surveyModel: sender,
        isSigner,
      });
    });

    surveyModel.onClearFiles.add(async (sender, options) => {
      await deleteFileHandler({
        options,
        urlSlug,
        publicId,
        surveyModel: sender,
        isSigner,
      });
    });
  }

  if (trackingId) {
    const urlSlug = settings.urlSlug;

    surveyModel.onUploadFiles.add(async (sender, options) => {
      await uploadFileHandler({
        options,
        urlSlug,
        trackingId,
      });
    });

    surveyModel.onClearFiles.add(async (sender, options) => {
      await deleteFileHandler({
        options,
        urlSlug,
        trackingId,
      });
    });
  }

  return surveyModel;
};
